import { useState, useEffect, useRef, useCallback } from "react";
import LoginScreen from "./components/LoginScreen";
import { onAuthChange, logoutUser, writeData, subscribe } from "./firebase";

// ─── Constants ────────────────────────────────────────────────────────────────
const CRIT_CYCLE = ["H","M","L"];
const CRITICALITY = {
  H:{label:"High",   bg:"#E8F5E9",text:"#2E7D32",border:"#A5D6A7",dot:"#4CAF50"},
  M:{label:"Medium", bg:"#FFFDE7",text:"#F57F17",border:"#FFE082",dot:"#FFC107"},
  L:{label:"Low",    bg:"#FFF3E0",text:"#E65100",border:"#FFCC80",dot:"#FF9800"},
};
const PALETTE  = ["#4285F4","#EA4335","#34A853","#FBBC04","#9C27B0","#00ACC1","#FF7043","#8D6E63","#5C6BC0","#26A69A"];
const ICONS    = ["☀️","💪","🧘","📚","💊","🎨","🎵","💧","🏃","🍎","😴","✍️","🌿","⭐","🎯","🧠","🏋️","🚴"];
const DAY_SH   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const MONTHS   = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const SEED_CATS = [
  {id:"1",name:"Morning Routine",color:"#FBBC04",icon:"☀️"},
  {id:"2",name:"Fitness",        color:"#34A853",icon:"💪"},
  {id:"3",name:"Mindfulness",    color:"#9C27B0",icon:"🧘"},
];
const SEED_HABITS = [
  {id:"h1",name:"Drink 8 glasses of water",  categoryId:"1",startDate:"2026-03-01",endDate:null,       color:"#4285F4",reminder:"08:00",criticality:"H"},
  {id:"h2",name:"Morning jog (30 min)",       categoryId:"2",startDate:"2026-03-01",endDate:null,       color:"#34A853",reminder:"07:00",criticality:"H"},
  {id:"h3",name:"Meditate 10 minutes",        categoryId:"3",startDate:"2026-03-01",endDate:null,       color:"#9C27B0",reminder:"09:00",criticality:"M"},
  {id:"h4",name:"Read 20 pages",              categoryId:"1",startDate:"2026-03-05",endDate:null,       color:"#FBBC04",reminder:"21:00",criticality:"M"},
  {id:"h5",name:"No social media before noon",categoryId:"1",startDate:"2026-03-01",endDate:"2026-12-31",color:"#EA4335",reminder:"12:00",criticality:"L"},
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const today    = new Date();
today.setHours(0,0,0,0);
const SIM_NOW  = `${String(new Date().getHours()).padStart(2,"0")}:${String(new Date().getMinutes()).padStart(2,"0")}`;

const fmt      = d=>`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
const addD     = (date,n)=>{const d=new Date(date);d.setDate(d.getDate()+n);return d;};
const same     = (a,b)=>fmt(a)===fmt(b);
const isApp    = (h,date)=>{const d=fmt(date);return d>=h.startDate&&(!h.endDate||d<=h.endDate);};
const genDays  = (center,n=14)=>Array.from({length:n},(_,i)=>addD(addD(center,-3),i));
const nextCrit = c=>CRIT_CYCLE[(CRIT_CYCLE.indexOf(c)+1)%3];

const fldSt = {background:"#F8F9FA",borderRadius:10,padding:"10px 12px",color:"#202124",fontSize:14,fontFamily:"inherit",border:"1px solid #DADCE0",width:"100%",outline:"none"};
const lblSt = {fontSize:11,color:"#5F6368",marginBottom:5,fontWeight:700,letterSpacing:0.6,textTransform:"uppercase"};

// ─── Drag handle ──────────────────────────────────────────────────────────────
function DragHandle({listeners}){
  return(
    <div {...listeners} style={{display:"flex",flexDirection:"column",gap:3,padding:"4px 3px",cursor:"grab",flexShrink:0,opacity:0.35,userSelect:"none",touchAction:"none"}} title="Drag to reorder">
      {[0,1,2].map(i=><div key={i} style={{width:14,height:2,borderRadius:1,background:"#5F6368"}}/>)}
    </div>
  );
}

// ─── useDragList ──────────────────────────────────────────────────────────────
function useDragList(items,onReorder){
  const ds=useRef({active:false,dragId:null,overId:null});
  const [dragId,setDragId]=useState(null);
  const [overId,setOverId]=useState(null);
  const ghostRef=useRef(null);
  const offsetRef=useRef({x:0,y:0});
  const itemEls=useRef({});

  const onPointerMove=useCallback(e=>{
    if(!ds.current.active)return;
    const g=ghostRef.current;
    if(g){g.style.left=(e.clientX-offsetRef.current.x)+"px";g.style.top=(e.clientY-offsetRef.current.y)+"px";}
    let closest=null,closestDist=Infinity;
    for(const id of Object.keys(itemEls.current)){
      const el=itemEls.current[id];if(!el)continue;
      const rect=el.getBoundingClientRect(),midY=rect.top+rect.height/2,dist=Math.abs(e.clientY-midY);
      if(dist<closestDist){closestDist=dist;closest=id;}
    }
    if(closest!==ds.current.overId){ds.current.overId=closest;setOverId(closest);}
  },[]);

  const onPointerUp=useCallback(()=>{
    if(!ds.current.active)return;
    const{dragId:dId,overId:oId}=ds.current;
    ds.current={active:false,dragId:null,overId:null};
    setDragId(null);setOverId(null);
    if(ghostRef.current){ghostRef.current.remove();ghostRef.current=null;}
    document.body.style.userSelect="";
    window.removeEventListener("pointermove",onPointerMove);
    window.removeEventListener("pointerup",onPointerUp);
    if(dId&&oId&&dId!==oId){
      const arr=[...items];
      const fi=arr.findIndex(x=>x.id===dId),ti=arr.findIndex(x=>x.id===oId);
      if(fi>=0&&ti>=0){const[m]=arr.splice(fi,1);arr.splice(ti,0,m);onReorder(arr);}
    }
  },[items,onReorder,onPointerMove]);

  const startDrag=useCallback((e,id)=>{
    if(e.button!==undefined&&e.button!==0)return;
    e.preventDefault();e.stopPropagation();
    const el=itemEls.current[id];if(!el)return;
    const rect=el.getBoundingClientRect();
    offsetRef.current={x:e.clientX-rect.left,y:e.clientY-rect.top};
    const g=el.cloneNode(true);
    g.style.cssText=`position:fixed;left:${rect.left}px;top:${rect.top}px;width:${rect.width}px;pointer-events:none;z-index:9999;opacity:0.92;box-shadow:0 8px 28px rgba(0,0,0,0.18);border-radius:13px;transform:rotate(1.5deg) scale(1.02);transition:transform 0.08s ease;`;
    document.body.appendChild(g);ghostRef.current=g;
    ds.current={active:true,dragId:id,overId:id};
    setDragId(id);setOverId(id);
    document.body.style.userSelect="none";
    window.addEventListener("pointermove",onPointerMove,{passive:true});
    window.addEventListener("pointerup",onPointerUp);
  },[onPointerMove,onPointerUp]);

  const getHandleListeners=useCallback(id=>({onPointerDown:e=>startDrag(e,id)}),[startDrag]);
  const getItemProps=useCallback(id=>({
    ref:el=>{itemEls.current[id]=el;},
    style:{opacity:id===dragId?0:1,outline:id===overId&&id!==dragId?"2px dashed #4285F4":"none",outlineOffset:2,transition:"outline 0.1s,opacity 0.1s"}
  }),[dragId,overId]);

  return{getItemProps,getHandleListeners};
}

// ─── SVG ──────────────────────────────────────────────────────────────────────
function MiniPie({done,total,color="#4285F4",size=16}){
  if(!total)return<div style={{width:size,height:size,borderRadius:"50%",background:"#E8EAED"}}/>;
  const pct=done/total,r=size/2-1,cx=size/2,cy=size/2;
  if(pct>=1)return<svg width={size} height={size}><circle cx={cx} cy={cy} r={r} fill={color}/></svg>;
  if(pct<=0)return<svg width={size} height={size}><circle cx={cx} cy={cy} r={r} fill="#E8EAED"/></svg>;
  const a=pct*2*Math.PI-Math.PI/2,x=cx+r*Math.cos(a),y=cy+r*Math.sin(a);
  return<svg width={size} height={size}><circle cx={cx} cy={cy} r={r} fill="#E8EAED"/><path d={`M${cx},${cy} L${cx},${cy-r} A${r},${r} 0 ${pct>0.5?1:0},1 ${x},${y} Z`} fill={color}/></svg>;
}

function Donut({done,total,size=88,color="#4285F4"}){
  const pct=total>0?done/total:0,r=(size-16)/2,circ=2*Math.PI*r,dash=pct*circ;
  return(
    <svg width={size} height={size}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#F1F3F4" strokeWidth={11}/>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={11}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`}
        style={{transition:"stroke-dasharray 0.55s cubic-bezier(0.34,1.56,0.64,1)"}}/>
      <text x={size/2} y={size/2+5} textAnchor="middle" style={{fontSize:14,fontWeight:700,fill:"#202124",fontFamily:"'Google Sans','Roboto',sans-serif"}}>{total>0?Math.round(pct*100):0}%</text>
      <text x={size/2} y={size/2+17} textAnchor="middle" style={{fontSize:9,fill:"#9AA0A6",fontFamily:"'Google Sans','Roboto',sans-serif"}}>{done}/{total}</text>
    </svg>
  );
}

// ─── ConfirmDialog ────────────────────────────────────────────────────────────
function ConfirmDialog({message,onConfirm,onCancel}){
  return(
    <div onClick={onCancel} style={{position:"fixed",inset:0,background:"rgba(32,33,36,0.55)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:18,padding:"24px 22px",width:"100%",maxWidth:340,boxShadow:"0 8px 40px rgba(0,0,0,0.18)"}}>
        <div style={{fontSize:16,fontWeight:700,color:"#202124",marginBottom:8}}>Confirm Delete</div>
        <div style={{fontSize:14,color:"#5F6368",marginBottom:22,lineHeight:1.55}}>{message}</div>
        <div style={{display:"flex",gap:10}}>
          <button onClick={onCancel}  style={{flex:1,padding:"11px",borderRadius:10,border:"1px solid #DADCE0",background:"#fff",color:"#5F6368",fontSize:14,fontWeight:600}}>Cancel</button>
          <button onClick={onConfirm} style={{flex:1,padding:"11px",borderRadius:10,border:"none",background:"#EA4335",color:"#fff",fontSize:14,fontWeight:700,boxShadow:"0 2px 8px rgba(234,67,53,0.28)"}}>Delete</button>
        </div>
      </div>
    </div>
  );
}

// ─── HabitEditSheet ───────────────────────────────────────────────────────────
function HabitEditSheet({habit,cats,onSave,onClose}){
  const[draft,setDraft]=useState({...habit,endDate:habit.endDate||""});
  const save=()=>{if(!draft.name.trim())return;onSave({...draft,endDate:draft.endDate||null});onClose();};
  return(
    <div className="modal-overlay" onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(32,33,36,0.52)",zIndex:200,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
      <div className="modal-box" onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:"22px 22px 0 0",padding:"22px 20px 36px",width:"100%",maxWidth:430,maxHeight:"92vh",overflowY:"auto",boxShadow:"0 -8px 40px rgba(0,0,0,0.14)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <span style={{fontSize:18,fontWeight:700,color:"#202124"}}>Edit Habit</span>
          <button onClick={onClose} style={{background:"#F1F3F4",border:"none",borderRadius:"50%",width:30,height:30,fontSize:16,color:"#5F6368",display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div><div style={lblSt}>Habit Name</div><input autoFocus value={draft.name} onChange={e=>setDraft(p=>({...p,name:e.target.value}))} style={fldSt} onKeyDown={e=>e.key==="Enter"&&save()}/></div>
          <div><div style={lblSt}>Category</div>
            <select value={draft.categoryId} onChange={e=>setDraft(p=>({...p,categoryId:e.target.value}))} style={fldSt}>
              <option value="">No category</option>
              {cats.map(c=><option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
            </select>
          </div>
          <div style={{display:"flex",gap:10}}>
            <div style={{flex:1}}><div style={lblSt}>Start Date</div><input type="date" value={draft.startDate} onChange={e=>setDraft(p=>({...p,startDate:e.target.value}))} style={fldSt}/></div>
            <div style={{flex:1}}><div style={lblSt}>End Date (opt.)</div><input type="date" value={draft.endDate} onChange={e=>setDraft(p=>({...p,endDate:e.target.value}))} style={fldSt}/></div>
          </div>
          <div style={{display:"flex",gap:10}}>
            <div style={{flex:1}}><div style={lblSt}>Reminder</div><input type="time" value={draft.reminder} onChange={e=>setDraft(p=>({...p,reminder:e.target.value}))} style={fldSt}/></div>
            <div style={{flex:1}}><div style={lblSt}>Criticality</div>
              <div style={{display:"flex",gap:6}}>
                {Object.entries(CRITICALITY).map(([k,v])=>(
                  <button key={k} onClick={()=>setDraft(p=>({...p,criticality:k}))} style={{flex:1,padding:"10px 0",borderRadius:9,border:`1.5px solid ${draft.criticality===k?v.border:"#E8EAED"}`,background:draft.criticality===k?v.bg:"#F8F9FA",color:v.text,fontWeight:700,fontSize:13,cursor:"pointer"}}>{k}</button>
                ))}
              </div>
            </div>
          </div>
          <div><div style={lblSt}>Accent Color</div>
            <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
              {PALETTE.map(c=><button key={c} onClick={()=>setDraft(p=>({...p,color:c}))} style={{width:28,height:28,borderRadius:"50%",background:c,border:draft.color===c?"3px solid #202124":"3px solid transparent",boxShadow:draft.color===c?`0 0 0 2px ${c}55`:"none",cursor:"pointer"}}/>)}
            </div>
          </div>
          <div style={{display:"flex",gap:10,marginTop:4}}>
            <button onClick={onClose} style={{flex:1,padding:"12px",borderRadius:12,border:"1px solid #DADCE0",background:"#fff",color:"#5F6368",fontSize:14,fontWeight:600,cursor:"pointer"}}>Cancel</button>
            <button onClick={save}    style={{flex:2,padding:"12px",borderRadius:12,border:"none",background:"#4285F4",color:"#fff",fontSize:15,fontWeight:700,cursor:"pointer",boxShadow:"0 2px 10px rgba(66,133,244,0.3)"}}>Save Changes</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── HabitCard ────────────────────────────────────────────────────────────────
function HabitCard({habit,catColor,done,onToggle,animating,streak,onDelete,onEdit,onCycleCrit,dragHandleListeners,dragProps}){
  const crit=CRITICALITY[habit.criticality]||CRITICALITY.M;
  const barColor=done?"#E8EAED":(catColor||habit.color);
  return(
    <div {...dragProps} className={"habit-item"+(animating?" pop-anim":"")} style={{background:done?"#FAFAFA":"#FFFFFF",borderRadius:13,padding:"11px 12px",display:"flex",alignItems:"center",gap:8,border:`1px solid ${done?"#F1F3F4":"#E8EAED"}`,boxShadow:done?"none":"0 1px 4px rgba(0,0,0,0.055)",opacity:done?0.78:1,...(dragProps?.style||{})}}>
      <DragHandle listeners={dragHandleListeners}/>
      <div style={{width:3,height:40,borderRadius:2,background:barColor,flexShrink:0}}/>
      <button className="check-btn" onClick={onToggle} style={{width:27,height:27,borderRadius:"50%",flexShrink:0,background:done?habit.color:"transparent",border:`2px solid ${done?habit.color:"#DADCE0"}`,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:done?`0 2px 8px ${habit.color}44`:"none"}}>
        {done&&<span style={{color:"#fff",fontSize:12,fontWeight:700}}>✓</span>}
      </button>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:14,fontWeight:600,color:done?"#9AA0A6":"#202124",textDecoration:done?"line-through":"none",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{habit.name}</div>
        <div style={{display:"flex",alignItems:"center",gap:7,marginTop:3,flexWrap:"wrap"}}>
          {habit.reminder&&<span style={{fontSize:10,color:done?"#BDBDBD":"#5F6368"}}>⏰ {habit.reminder}</span>}
          {streak>0&&!done&&<span style={{fontSize:10,color:"#FBBC04",fontWeight:600}}>🔥 {streak}d streak</span>}
        </div>
      </div>
      <button onClick={e=>{e.stopPropagation();onCycleCrit();}} onMouseDown={e=>e.currentTarget.style.transform="scale(0.85)"} onMouseUp={e=>e.currentTarget.style.transform="scale(1)"}
        style={{padding:"3px 8px",borderRadius:7,fontSize:11,fontWeight:700,flexShrink:0,background:crit.bg,color:crit.text,border:`1px solid ${crit.border}`,cursor:"pointer",transition:"transform 0.12s ease"}}>{habit.criticality}</button>
      <button onClick={e=>{e.stopPropagation();onEdit();}} onMouseEnter={e=>e.currentTarget.style.color="#4285F4"} onMouseLeave={e=>e.currentTarget.style.color="#BDBDBD"} style={{background:"none",border:"none",padding:"3px",fontSize:14,color:"#BDBDBD",lineHeight:1,transition:"color 0.15s",cursor:"pointer"}}>✏️</button>
      <button onClick={e=>{e.stopPropagation();onDelete();}} onMouseEnter={e=>e.currentTarget.style.color="#EA4335"} onMouseLeave={e=>e.currentTarget.style.color="#BDBDBD"} style={{background:"none",border:"none",padding:"3px",fontSize:14,color:"#BDBDBD",lineHeight:1,transition:"color 0.15s",cursor:"pointer"}}>🗑</button>
    </div>
  );
}

// ─── CategoryEditorRow ────────────────────────────────────────────────────────
function CategoryEditorRow({cat,habits,onSave,onDelete,dragHandleListeners}){
  const[editing,setEditing]=useState(false);
  const[draft,setDraft]=useState({name:cat.name,icon:cat.icon,color:cat.color});
  const[nameOnly,setNameOnly]=useState(false);
  const[nameDraft,setNameDraft]=useState(cat.name);
  const[confirmDel,setConfirmDel]=useState(false);
  const nameRef=useRef(null);
  useEffect(()=>{setDraft({name:cat.name,icon:cat.icon,color:cat.color});setNameDraft(cat.name);},[cat]);
  const startNameEdit=()=>{setNameDraft(cat.name);setNameOnly(true);setTimeout(()=>nameRef.current?.focus(),30);};
  const commitName=()=>{const t=nameDraft.trim();if(t&&t!==cat.name)onSave({...cat,name:t});setNameOnly(false);};
  const save=()=>{if(draft.name.trim()){onSave({...cat,...draft});setEditing(false);}};
  const cancel=()=>{setDraft({name:cat.name,icon:cat.icon,color:cat.color});setEditing(false);};
  return(
    <>
      {confirmDel&&<ConfirmDialog message={`Delete category "${cat.name}"? Its habits will become uncategorised.`} onConfirm={onDelete} onCancel={()=>setConfirmDel(false)}/>}
      <div style={{background:"#fff",borderRadius:14,border:"1px solid #E8EAED",marginBottom:4,overflow:"hidden",boxShadow:"0 1px 4px rgba(0,0,0,0.05)"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,padding:"13px 14px"}}>
          <DragHandle listeners={dragHandleListeners}/>
          <div style={{width:38,height:38,borderRadius:10,background:cat.color+"22",display:"flex",alignItems:"center",justifyContent:"center",fontSize:21,flexShrink:0}}>{cat.icon}</div>
          <div style={{flex:1,minWidth:0}}>
            {nameOnly?(
              <input ref={nameRef} value={nameDraft} onChange={e=>setNameDraft(e.target.value)} onBlur={commitName} onKeyDown={e=>{if(e.key==="Enter")commitName();if(e.key==="Escape"){setNameOnly(false);setNameDraft(cat.name);}}} style={{width:"100%",fontSize:15,fontWeight:700,color:"#202124",background:"#F0F4FF",border:"1.5px solid #4285F4",borderRadius:8,padding:"3px 8px",outline:"none",fontFamily:"inherit"}}/>
            ):(
              <div onDoubleClick={startNameEdit} style={{fontSize:15,fontWeight:700,color:"#202124",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",cursor:"text"}}>{cat.name}</div>
            )}
            <div style={{display:"flex",alignItems:"center",gap:6,marginTop:2}}>
              <div style={{width:9,height:9,borderRadius:"50%",background:cat.color}}/>
              <span style={{fontSize:11,color:"#9AA0A6"}}>{habits.length} habit{habits.length!==1?"s":""}</span>
            </div>
          </div>
          <button onClick={startNameEdit} onMouseEnter={e=>e.currentTarget.style.color="#4285F4"} onMouseLeave={e=>e.currentTarget.style.color="#BDBDBD"} style={{background:"none",border:"none",fontSize:15,color:"#BDBDBD",padding:"4px",cursor:"pointer",transition:"color 0.15s",lineHeight:1}}>✏️</button>
          <button onClick={()=>{if(editing)cancel();else{setDraft({name:cat.name,icon:cat.icon,color:cat.color});setEditing(true);}}} style={{background:editing?"#E8F0FE":"#F1F3F4",border:`1px solid ${editing?"#4285F4":"#DADCE0"}`,borderRadius:9,padding:"7px 13px",fontSize:12,fontWeight:600,color:editing?"#4285F4":"#5F6368",cursor:"pointer"}}>{editing?"Close":"Edit"}</button>
          <button onClick={()=>setConfirmDel(true)} style={{background:"#FFF3F3",border:"1px solid #FFCDD2",borderRadius:9,padding:"7px 10px",fontSize:13,color:"#EA4335",cursor:"pointer"}}>🗑</button>
        </div>
        {editing&&(
          <div style={{borderTop:"1px solid #F1F3F4",padding:"14px 14px 16px",background:"#FAFAFA"}}>
            <div style={{marginBottom:12}}><div style={lblSt}>Name</div><input value={draft.name} onChange={e=>setDraft(p=>({...p,name:e.target.value}))} autoFocus style={{...fldSt,fontSize:14}} onKeyDown={e=>{if(e.key==="Enter")save();if(e.key==="Escape")cancel();}}/></div>
            <div style={{marginBottom:12}}><div style={lblSt}>Icon</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                {ICONS.map(ic=><button key={ic} onClick={()=>setDraft(p=>({...p,icon:ic}))} style={{background:draft.icon===ic?"#E8F0FE":"#F8F9FA",border:`1.5px solid ${draft.icon===ic?"#4285F4":"#E8EAED"}`,borderRadius:8,padding:"5px 7px",fontSize:17,cursor:"pointer"}}>{ic}</button>)}
              </div>
            </div>
            <div style={{marginBottom:16}}><div style={lblSt}>Color</div>
              <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
                {PALETTE.map(c=><button key={c} onClick={()=>setDraft(p=>({...p,color:c}))} style={{width:30,height:30,borderRadius:"50%",background:c,cursor:"pointer",border:draft.color===c?"3px solid #202124":"3px solid transparent",boxShadow:draft.color===c?`0 0 0 2px ${c}55`:"none"}}/>)}
              </div>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={cancel} style={{flex:1,padding:"11px",borderRadius:10,border:"1px solid #DADCE0",background:"#fff",color:"#5F6368",fontSize:14,fontWeight:600,cursor:"pointer"}}>Cancel</button>
              <button onClick={save}   style={{flex:2,padding:"11px",borderRadius:10,border:"none",background:"#4285F4",color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",boxShadow:"0 2px 8px rgba(66,133,244,0.28)"}}>Save Changes</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ─── DraggableHabitList ───────────────────────────────────────────────────────
function DraggableHabitList({habits,onReorder,renderCard}){
  const handle=useCallback(o=>onReorder(o),[onReorder]);
  const{getItemProps,getHandleListeners}=useDragList(habits,handle);
  return(
    <div style={{display:"flex",flexDirection:"column",gap:8}}>
      {habits.map(h=>{
        const ip=getItemProps(h.id),hl=getHandleListeners(h.id);
        return <div key={h.id} {...ip}>{renderCard(h,hl)}</div>;
      })}
    </div>
  );
}

function DraggableCatList({cats,onReorder,renderRow}){
  const handle=useCallback(o=>onReorder(o),[onReorder]);
  const{getItemProps,getHandleListeners}=useDragList(cats,handle);
  return(
    <div>
      {cats.map(c=>{
        const ip=getItemProps(c.id),hl=getHandleListeners(c.id);
        return <div key={c.id} {...ip}>{renderRow(c,hl)}</div>;
      })}
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function App(){
  const[user,setUser]=useState(undefined); // undefined=loading, null=signed out

  // ── Auth listener ──
  useEffect(()=>{
    const unsub=onAuthChange(u=>setUser(u||null));
    return unsub;
  },[]);

  if(user===undefined) return(
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#F8F9FB",fontFamily:"'Google Sans','Roboto',sans-serif"}}>
      <div style={{fontSize:40,animation:"spin 1s linear infinite"}}>⟳</div>
      <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if(!user) return <LoginScreen/>;

  return <HabitTracker uid={user.uid} userEmail={user.email}/>;
}

// ─── HabitTracker (requires uid for Firebase sync) ───────────────────────────
function HabitTracker({uid,userEmail}){
  const[habits,      setHabits]      = useState(null);
  const[cats,        setCats]        = useState(null);
  const[doneMap,     setDoneMap]     = useState(null);
  const[loaded,      setLoaded]      = useState(false);
  const[activeTab,   setActiveTab]   = useState("today");
  const[selDate,     setSelDate]     = useState(today);
  const[collapsed,   setCollapsed]   = useState(false);
  const[calCenter,   setCalCenter]   = useState(today);
  const[showAddH,    setShowAddH]    = useState(false);
  const[showAddC,    setShowAddC]    = useState(false);
  const[editHabit,   setEditHabit]   = useState(null);
  const[trendsOpen,  setTrendsOpen]  = useState(null);
  const[trendsMonth, setTrendsMonth] = useState({year:today.getFullYear(),month:today.getMonth()});
  const[animId,      setAnimId]      = useState(null);
  const[dismissed,   setDismissed]   = useState(new Set());
  const[confirmDel,  setConfirmDel]  = useState(null);
  const[newH,        setNewH]        = useState({name:"",categoryId:"",startDate:fmt(today),endDate:"",color:"#4285F4",reminder:"",criticality:"M"});
  const[newC,        setNewC]        = useState({name:"",icon:"⭐",color:"#4285F4"});

  // ── Load data from Firebase ──
  useEffect(()=>{
    const unsubs=[];
    let habitsReady=false,catsReady=false,doneReady=false;
    const check=()=>{ if(habitsReady&&catsReady&&doneReady) setLoaded(true); };

    unsubs.push(subscribe(uid,"habits",val=>{
      if(val){ setHabits(Object.values(val)); }
      else { setHabits(SEED_HABITS); writeData(uid,"habits",Object.fromEntries(SEED_HABITS.map(h=>[h.id,h]))); }
      habitsReady=true; check();
    }));
    unsubs.push(subscribe(uid,"categories",val=>{
      if(val){ setCats(Object.values(val)); }
      else { setCats(SEED_CATS); writeData(uid,"categories",Object.fromEntries(SEED_CATS.map(c=>[c.id,c]))); }
      catsReady=true; check();
    }));
    unsubs.push(subscribe(uid,"completions",val=>{
      setDoneMap(val||{});
      doneReady=true; check();
    }));
    return ()=>unsubs.forEach(u=>u());
  },[uid]);

  // ── Sync helpers ──
  const syncHabits = useCallback(arr=>{
    setHabits(arr);
    writeData(uid,"habits",Object.fromEntries(arr.map(h=>[h.id,h])));
  },[uid]);
  const syncCats = useCallback(arr=>{
    setCats(arr);
    writeData(uid,"categories",Object.fromEntries(arr.map(c=>[c.id,c])));
  },[uid]);
  const syncDone = useCallback(map=>{
    setDoneMap(map);
    writeData(uid,"completions",map);
  },[uid]);

  const reorderHabits=useCallback(slice=>{
    setHabits(prev=>{
      const ids=new Set(slice.map(h=>h.id));
      const rest=prev.filter(h=>!ids.has(h.id));
      const fi=prev.findIndex(h=>ids.has(h.id));
      const result=[...rest.slice(0,fi),...slice,...rest.slice(fi)];
      writeData(uid,"habits",Object.fromEntries(result.map(h=>[h.id,h])));
      return result;
    });
  },[uid]);

  const reorderCats=useCallback(arr=>{
    syncCats(arr);
  },[syncCats]);

  if(!loaded||!habits||!cats||doneMap===null){
    return(
      <div style={{minHeight:"100vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",background:"#F8F9FB",fontFamily:"'Google Sans','Roboto',sans-serif",gap:12}}>
        <div style={{fontSize:40,animation:"spin 1s linear infinite"}}>⟳</div>
        <div style={{fontSize:14,color:"#9AA0A6"}}>Loading your habits…</div>
        <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  const isDone  = (hid,date)=>!!(doneMap[`${hid}_${fmt(date)}`]);
  const toggle  = (hid,date)=>{
    setAnimId(hid);setTimeout(()=>setAnimId(null),450);
    const key=`${hid}_${fmt(date)}`,newMap={...doneMap,[key]:!doneMap[key]};
    syncDone(newMap);
  };
  const streak  = h=>{let s=0,d=addD(today,-1);while(isApp(h,d)&&isDone(h.id,d)){s++;d=addD(d,-1);}return s;};
  const getCat  = id=>cats.find(c=>c.id===id);

  const calDays  = genDays(calCenter,14);
  const appH     = habits.filter(h=>isApp(h,selDate));
  const pending  = appH.filter(h=>!isDone(h.id,selDate));
  const doneList = appH.filter(h=>isDone(h.id,selDate));
  const alerts   = habits.filter(h=>isApp(h,today)&&h.reminder&&h.reminder<=SIM_NOW&&!isDone(h.id,today)&&!dismissed.has(h.id));
  const domColor = appH.length?(getCat(appH[0].categoryId)?.color||"#4285F4"):"#4285F4";

  const addHabit    = ()=>{if(!newH.name.trim())return;const h={...newH,id:"h"+Date.now(),endDate:newH.endDate||null};syncHabits([...habits,h]);setNewH({name:"",categoryId:"",startDate:fmt(today),endDate:"",color:"#4285F4",reminder:"",criticality:"M"});setShowAddH(false);};
  const saveHabit   = upd=>syncHabits(habits.map(h=>h.id===upd.id?upd:h));
  const deleteHabit = id=>{syncHabits(habits.filter(h=>h.id!==id));setConfirmDel(null);};
  const cycleCrit   = id=>syncHabits(habits.map(h=>h.id===id?{...h,criticality:nextCrit(h.criticality)}:h));
  const addCat      = ()=>{if(!newC.name.trim())return;const c={...newC,id:"c"+Date.now()};syncCats([...cats,c]);setNewC({name:"",icon:"⭐",color:"#4285F4"});setShowAddC(false);};
  const saveCat     = upd=>syncCats(cats.map(c=>c.id===upd.id?upd:c));
  const deleteCat   = id=>{syncCats(cats.filter(c=>c.id!==id));syncHabits(habits.map(h=>h.categoryId===id?{...h,categoryId:""}:h));setConfirmDel(null);};

  const getTrendsCells=hab=>{
    const{year,month}=trendsMonth,first=new Date(year,month,1),cells=[];
    for(let i=0;i<first.getDay();i++)cells.push(null);
    for(let d=1;d<=new Date(year,month+1,0).getDate();d++){
      const date=new Date(year,month,d),app=isApp(hab,date);
      cells.push({date,applicable:app,done:app?isDone(hab.id,date):null});
    }
    return cells;
  };

  const makeCard=(h,handleListeners)=>{
    const cat=getCat(h.categoryId);
    return(
      <HabitCard habit={h} catColor={cat?.color||null} done={isDone(h.id,selDate)} animating={animId===h.id}
        onToggle={()=>toggle(h.id,selDate)} streak={streak(h)}
        onDelete={()=>setConfirmDel({type:"habit",id:h.id})}
        onEdit={()=>setEditHabit(h)} onCycleCrit={()=>cycleCrit(h.id)}
        dragHandleListeners={handleListeners}/>
    );
  };

  return(
    <div style={{fontFamily:"'Google Sans','Roboto','Segoe UI',sans-serif",background:"#F8F9FB",minHeight:"100vh",color:"#202124",maxWidth:430,margin:"0 auto"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Google+Sans:wght@400;500;700&family=Roboto:wght@300;400;500;700&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        body{background:#F8F9FB}
        ::-webkit-scrollbar{width:3px}::-webkit-scrollbar-thumb{background:#DADCE0;border-radius:2px}
        .scroll-row{overflow-x:auto;scrollbar-width:none}.scroll-row::-webkit-scrollbar{display:none}
        .habit-item{transition:box-shadow 0.18s ease}
        .check-btn{transition:all 0.22s cubic-bezier(0.34,1.56,0.64,1);cursor:pointer}.check-btn:active{transform:scale(0.72)}
        .modal-overlay{animation:fadeIn 0.14s ease}.modal-box{animation:slideUp 0.22s cubic-bezier(0.34,1.56,0.64,1)}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        @keyframes slideUp{from{transform:translateY(32px);opacity:0}to{transform:translateY(0);opacity:1}}
        @keyframes pop{0%{transform:scale(1)}50%{transform:scale(1.15)}100%{transform:scale(1)}}
        .pop-anim{animation:pop 0.38s ease}
        input::placeholder{color:#9AA0A6}
        button{font-family:inherit;cursor:pointer}
      `}</style>

      {/* ── STICKY HEADER ── */}
      <div style={{background:"linear-gradient(155deg,#E8F0FE 0%,#F8F9FB 60%)",borderBottom:"1px solid #E8EAED",position:"sticky",top:0,zIndex:20}}>
        <div style={{padding:"44px 18px 0"}}>
          {/* User bar */}
          <div style={{display:"flex",justifyContent:"flex-end",alignItems:"center",gap:10,marginBottom:10}}>
            <div style={{width:8,height:8,borderRadius:"50%",background:"#34A853",boxShadow:"0 0 0 2px #34A85333"}} title="Synced"/>
            <span style={{fontSize:11,color:"#9AA0A6"}}>{userEmail}</span>
            <button onClick={logoutUser} style={{background:"#fff",border:"1px solid #DADCE0",borderRadius:8,padding:"4px 10px",fontSize:11,color:"#5F6368",fontWeight:600}}>Sign out</button>
          </div>

          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
            <div style={{display:"flex",alignItems:"center",gap:14}}>
              <Donut done={doneList.length} total={appH.length} color={domColor} size={86}/>
              <div>
                <div style={{fontSize:22,fontWeight:700,color:"#202124",lineHeight:1.15}}>{same(selDate,today)?"Today":`${DAY_SH[selDate.getDay()]}, ${selDate.getDate()}`}</div>
                <div style={{fontSize:12,color:"#9AA0A6",marginTop:2}}>{MONTHS[selDate.getMonth()]} {selDate.getFullYear()}</div>
                <div style={{fontSize:12,color:"#9AA0A6",marginTop:1}}>{doneList.length}/{appH.length} complete</div>
              </div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:7,alignItems:"flex-end"}}>
              <button onClick={()=>setShowAddH(true)} style={{background:"#4285F4",border:"none",borderRadius:10,padding:"8px 14px",color:"#fff",fontSize:12,fontWeight:600,boxShadow:"0 2px 10px rgba(66,133,244,0.3)"}}>+ Habit</button>
              <button onClick={()=>setShowAddC(true)} style={{background:"#fff",border:"1px solid #DADCE0",borderRadius:10,padding:"7px 14px",color:"#5F6368",fontSize:12,fontWeight:500}}>+ Category</button>
            </div>
          </div>

          {/* Date strip */}
          <div className="scroll-row" style={{display:"flex",gap:6,paddingBottom:14}}>
            <button onClick={()=>setCalCenter(c=>addD(c,-7))} style={{background:"#fff",border:"1px solid #DADCE0",borderRadius:10,padding:"0 9px",color:"#9AA0A6",fontSize:14,flexShrink:0,height:68}}>‹</button>
            {calDays.map((d,i)=>{
              const isSel=same(d,selDate),isTod=same(d,today);
              const app=habits.filter(h=>isApp(h,d)),dC=app.filter(h=>isDone(h.id,d)).length;
              return(
                <button key={i} onClick={()=>setSelDate(d)} style={{flexShrink:0,width:50,height:68,borderRadius:12,background:isSel?"#4285F4":"#fff",border:isTod&&!isSel?"2px solid #4285F4":"1px solid #E8EAED",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"space-between",padding:"8px 0",boxShadow:isSel?"0 4px 12px rgba(66,133,244,0.28)":"0 1px 3px rgba(0,0,0,0.04)",transition:"all 0.15s ease"}}>
                  <span style={{fontSize:10,fontWeight:600,color:isSel?"rgba(255,255,255,0.7)":"#9AA0A6"}}>{DAY_SH[d.getDay()]}</span>
                  <span style={{fontSize:15,fontWeight:700,color:isSel?"#fff":isTod?"#4285F4":"#374151"}}>{d.getDate()}</span>
                  <div style={{height:18,display:"flex",alignItems:"center",justifyContent:"center"}}>
                    {app.length>0?<MiniPie done={dC} total={app.length} color={isSel?"#fff":"#4285F4"} size={16}/>:<div style={{width:4,height:4,borderRadius:"50%",background:isSel?"rgba(255,255,255,0.3)":"#E8EAED"}}/>}
                  </div>
                </button>
              );
            })}
            <button onClick={()=>setCalCenter(c=>addD(c,7))} style={{background:"#fff",border:"1px solid #DADCE0",borderRadius:10,padding:"0 9px",color:"#9AA0A6",fontSize:14,flexShrink:0,height:68}}>›</button>
          </div>
        </div>

        <div style={{display:"flex",margin:"0 16px",background:"#E8F0FE",borderRadius:12,padding:3,gap:2}}>
          {[["today","Habits"],["trends","Trends"],["categories","Categories"]].map(([val,lbl])=>(
            <button key={val} onClick={()=>setActiveTab(val)} style={{flex:1,padding:"8px 4px",borderRadius:9,border:"none",fontWeight:600,fontSize:12,background:activeTab===val?"#4285F4":"transparent",color:activeTab===val?"#fff":"#5F6368",boxShadow:activeTab===val?"0 2px 6px rgba(66,133,244,0.22)":"none",transition:"all 0.17s ease"}}>{lbl}</button>
          ))}
        </div>
        <div style={{height:12}}/>
      </div>

      {/* ── ALERTS ── */}
      {same(selDate,today)&&alerts.length>0&&(
        <div style={{margin:"12px 16px 0",display:"flex",flexDirection:"column",gap:6}}>
          {alerts.map(h=>(
            <div key={h.id} style={{background:"#FFFBEB",border:"1px solid #FCD34D",borderRadius:11,padding:"10px 14px",display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:18}}>⏰</span>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:700,color:"#92400E"}}>Reminder Overdue</div>
                <div style={{fontSize:12,color:"#78350F"}}><b>{h.name}</b> was due at {h.reminder}</div>
              </div>
              <button onClick={()=>setDismissed(s=>new Set([...s,h.id]))} style={{background:"none",border:"none",fontSize:18,color:"#92400E",padding:0}}>×</button>
            </div>
          ))}
        </div>
      )}

      {/* ── TAB CONTENT ── */}
      <div style={{padding:"14px 16px 100px"}}>

        {activeTab==="today"&&(
          <>
            {appH.length===0&&(
              <div style={{textAlign:"center",padding:"48px 0",color:"#9AA0A6"}}>
                <div style={{fontSize:40,marginBottom:10}}>🌱</div>
                <div style={{fontSize:14}}>No habits for this date</div>
              </div>
            )}
            {cats.map(cat=>{
              const catH=pending.filter(h=>h.categoryId===cat.id);
              if(!catH.length)return null;
              return(
                <div key={cat.id} style={{marginBottom:20}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:9}}>
                    <div style={{width:9,height:9,borderRadius:"50%",background:cat.color,flexShrink:0}}/>
                    <span style={{fontSize:11,fontWeight:700,letterSpacing:1.1,textTransform:"uppercase",color:cat.color}}>{cat.icon} {cat.name}</span>
                    <div style={{flex:1,height:1,background:cat.color+"28"}}/>
                  </div>
                  <DraggableHabitList habits={catH} onReorder={reorderHabits} renderCard={(h,hl)=>makeCard(h,hl)}/>
                </div>
              );
            })}
            {(()=>{const u=pending.filter(h=>!h.categoryId);if(!u.length)return null;return(
              <div style={{marginBottom:20}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:9}}>
                  <span style={{fontSize:11,fontWeight:700,letterSpacing:1.1,textTransform:"uppercase",color:"#9AA0A6"}}>Other</span>
                  <div style={{flex:1,height:1,background:"#E8EAED"}}/>
                </div>
                <DraggableHabitList habits={u} onReorder={reorderHabits} renderCard={(h,hl)=>makeCard(h,hl)}/>
              </div>
            );})()}
            {doneList.length>0&&(
              <div>
                <button onClick={()=>setCollapsed(c=>!c)} style={{display:"flex",alignItems:"center",gap:8,width:"100%",background:"none",border:"none",marginBottom:9,padding:0}}>
                  <span style={{fontSize:11,fontWeight:700,letterSpacing:1.1,textTransform:"uppercase",color:"#34A853"}}>✓ Completed ({doneList.length})</span>
                  <div style={{flex:1,height:1,background:"#34A85322"}}/>
                  <span style={{color:"#34A853",fontSize:14,display:"inline-block",transform:collapsed?"rotate(-90deg)":"rotate(0)",transition:"transform 0.2s"}}>▾</span>
                </button>
                {!collapsed&&<DraggableHabitList habits={doneList} onReorder={reorderHabits} renderCard={(h,hl)=>makeCard(h,hl)}/>}
              </div>
            )}
          </>
        )}

        {activeTab==="trends"&&(
          <div>
            {habits.map(hab=>{
              const cat=getCat(hab.categoryId),cells=getTrendsCells(hab);
              const appC=cells.filter(c=>c?.applicable),dC=appC.filter(c=>c.done).length;
              const isOpen=trendsOpen===hab.id,crit=CRITICALITY[hab.criticality]||CRITICALITY.M;
              return(
                <div key={hab.id} onClick={()=>setTrendsOpen(isOpen?null:hab.id)} style={{background:"#fff",borderRadius:15,padding:16,marginBottom:10,border:"1px solid #E8EAED",boxShadow:"0 1px 4px rgba(0,0,0,0.05)",cursor:"pointer"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0,flex:1}}>
                      <div style={{width:10,height:10,borderRadius:"50%",background:cat?.color||hab.color,flexShrink:0}}/>
                      <div style={{minWidth:0}}>
                        <div style={{fontSize:14,fontWeight:600,color:"#202124",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{hab.name}</div>
                        <div style={{display:"flex",gap:6,marginTop:2,alignItems:"center"}}>
                          {cat&&<span style={{fontSize:11,color:cat.color}}>{cat.icon} {cat.name}</span>}
                          <span style={{padding:"1px 5px",borderRadius:5,fontSize:10,fontWeight:700,background:crit.bg,color:crit.text,border:`1px solid ${crit.border}`}}>{hab.criticality}</span>
                          {hab.reminder&&<span style={{fontSize:11,color:"#9AA0A6"}}>⏰ {hab.reminder}</span>}
                        </div>
                      </div>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0,marginLeft:10}}>
                      <div style={{textAlign:"right"}}>
                        <div style={{fontSize:19,fontWeight:700,color:cat?.color||hab.color}}>{appC.length>0?Math.round(dC/appC.length*100):0}%</div>
                        <div style={{fontSize:10,color:"#9AA0A6"}}>{dC}/{appC.length}</div>
                      </div>
                      <span style={{color:"#DADCE0",fontSize:16,display:"inline-block",transform:isOpen?"rotate(180deg)":"none",transition:"transform 0.2s"}}>▾</span>
                    </div>
                  </div>
                  {isOpen&&(
                    <div onClick={e=>e.stopPropagation()} style={{marginTop:16}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
                        <button onClick={()=>setTrendsMonth(m=>{const d=new Date(m.year,m.month-1,1);return{year:d.getFullYear(),month:d.getMonth()};})} style={{background:"#F1F3F4",border:"none",borderRadius:8,padding:"5px 10px",color:"#9AA0A6"}}>‹</button>
                        <span style={{fontSize:13,fontWeight:600,color:"#202124"}}>{MONTHS[trendsMonth.month]} {trendsMonth.year}</span>
                        <button onClick={()=>setTrendsMonth(m=>{const d=new Date(m.year,m.month+1,1);return{year:d.getFullYear(),month:d.getMonth()};})} style={{background:"#F1F3F4",border:"none",borderRadius:8,padding:"5px 10px",color:"#9AA0A6"}}>›</button>
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3,marginBottom:3}}>
                        {DAY_SH.map(d=><div key={d} style={{textAlign:"center",fontSize:10,color:"#9AA0A6",fontWeight:700}}>{d[0]}</div>)}
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3}}>
                        {cells.map((cell,i)=>{
                          const isFut=cell&&cell.date>today,isT=cell&&same(cell.date,today);
                          const fillColor=cat?.color||hab.color;
                          const bg=!cell?"transparent":!cell.applicable?"#F8F9FA":cell.done?fillColor:isFut||isT?"#F1F3F4":"#FCECEA";
                          const fc=!cell?"transparent":!cell.applicable?"#DADCE0":cell.done?"#fff":isFut||isT?"#DADCE0":"#EA4335";
                          return<div key={i} style={{aspectRatio:"1",borderRadius:5,background:bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:600,color:fc,border:isT?"1.5px solid #4285F4":"none"}}>{cell?cell.date.getDate():""}</div>;
                        })}
                      </div>
                      <div style={{display:"flex",gap:14,marginTop:10,justifyContent:"center"}}>
                        {[[cat?.color||hab.color,"Done"],["#FCECEA","Missed"],["#F8F9FA","N/A"]].map(([bg,lbl])=>(
                          <div key={lbl} style={{display:"flex",alignItems:"center",gap:4}}>
                            <div style={{width:10,height:10,borderRadius:3,background:bg,border:"1px solid #E8EAED"}}/>
                            <span style={{fontSize:10,color:"#9AA0A6"}}>{lbl}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {activeTab==="categories"&&(
          <div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
              <div style={{fontSize:13,color:"#9AA0A6",fontWeight:500}}>{cats.length} categories · {habits.length} habits total</div>
              <button onClick={()=>setShowAddC(true)} style={{background:"#4285F4",border:"none",borderRadius:10,padding:"8px 14px",color:"#fff",fontSize:12,fontWeight:600,boxShadow:"0 2px 8px rgba(66,133,244,0.28)"}}>+ New Category</button>
            </div>
            {cats.length===0&&(
              <div style={{textAlign:"center",padding:"40px 0",color:"#9AA0A6"}}>
                <div style={{fontSize:36,marginBottom:8}}>📂</div>
                <div style={{fontSize:14}}>No categories yet</div>
              </div>
            )}
            <DraggableCatList cats={cats} onReorder={reorderCats} renderRow={(cat,hl)=>{
              const catHabits=habits.filter(h=>h.categoryId===cat.id);
              return(
                <div key={cat.id} style={{marginBottom:16}}>
                  <CategoryEditorRow cat={cat} habits={catHabits} onSave={saveCat} onDelete={()=>deleteCat(cat.id)} dragHandleListeners={hl}/>
                  {catHabits.length>0?(
                    <div style={{marginLeft:16,marginTop:2,paddingLeft:12,borderLeft:`2px solid ${cat.color}33`,paddingBottom:4}}>
                      {catHabits.map(h=>(
                        <div key={h.id} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 0",borderBottom:"1px solid #F8F9FA"}}>
                          <div style={{width:6,height:6,borderRadius:"50%",background:cat.color,flexShrink:0}}/>
                          <span style={{fontSize:13,color:"#5F6368",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{h.name}</span>
                          {h.reminder&&<span style={{fontSize:10,color:"#9AA0A6"}}>⏰ {h.reminder}</span>}
                          <span style={{fontSize:10,fontWeight:700,padding:"2px 6px",borderRadius:5,background:CRITICALITY[h.criticality]?.bg,color:CRITICALITY[h.criticality]?.text,border:`1px solid ${CRITICALITY[h.criticality]?.border}`,flexShrink:0}}>{h.criticality}</span>
                        </div>
                      ))}
                    </div>
                  ):(
                    <div style={{marginLeft:16,paddingLeft:12,borderLeft:`2px solid ${cat.color}22`,paddingBottom:4}}>
                      <span style={{fontSize:12,color:"#DADCE0",fontStyle:"italic"}}>No habits in this category</span>
                    </div>
                  )}
                </div>
              );
            }}/>
          </div>
        )}
      </div>

      {/* ── MODALS ── */}
      {editHabit&&<HabitEditSheet habit={editHabit} cats={cats} onSave={saveHabit} onClose={()=>setEditHabit(null)}/>}

      {confirmDel&&(
        <ConfirmDialog
          message={confirmDel.type==="habit"?`Delete "${habits.find(h=>h.id===confirmDel.id)?.name}"? This cannot be undone.`:`Delete category "${cats.find(c=>c.id===confirmDel.id)?.name}"?`}
          onConfirm={()=>confirmDel.type==="habit"?deleteHabit(confirmDel.id):deleteCat(confirmDel.id)}
          onCancel={()=>setConfirmDel(null)}
        />
      )}

      {showAddH&&(
        <div className="modal-overlay" onClick={()=>setShowAddH(false)} style={{position:"fixed",inset:0,background:"rgba(32,33,36,0.48)",zIndex:100,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
          <div className="modal-box" onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:"22px 22px 0 0",padding:"22px 20px 32px",width:"100%",maxWidth:430,maxHeight:"92vh",overflowY:"auto",boxShadow:"0 -8px 40px rgba(0,0,0,0.14)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
              <span style={{fontSize:18,fontWeight:700,color:"#202124"}}>New Habit</span>
              <button onClick={()=>setShowAddH(false)} style={{background:"#F1F3F4",border:"none",borderRadius:"50%",width:30,height:30,fontSize:16,color:"#5F6368",display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <div><div style={lblSt}>Habit Name</div><input value={newH.name} onChange={e=>setNewH(p=>({...p,name:e.target.value}))} placeholder="e.g. Drink 8 glasses of water" style={fldSt} autoFocus/></div>
              <div><div style={lblSt}>Category</div>
                <select value={newH.categoryId} onChange={e=>setNewH(p=>({...p,categoryId:e.target.value}))} style={fldSt}>
                  <option value="">No category</option>
                  {cats.map(c=><option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                </select>
              </div>
              <div style={{display:"flex",gap:10}}>
                <div style={{flex:1}}><div style={lblSt}>Start Date</div><input type="date" value={newH.startDate} onChange={e=>setNewH(p=>({...p,startDate:e.target.value}))} style={fldSt}/></div>
                <div style={{flex:1}}><div style={lblSt}>End Date (opt.)</div><input type="date" value={newH.endDate} onChange={e=>setNewH(p=>({...p,endDate:e.target.value}))} style={fldSt}/></div>
              </div>
              <div style={{display:"flex",gap:10}}>
                <div style={{flex:1}}><div style={lblSt}>Reminder</div><input type="time" value={newH.reminder} onChange={e=>setNewH(p=>({...p,reminder:e.target.value}))} style={fldSt}/></div>
                <div style={{flex:1}}><div style={lblSt}>Criticality</div>
                  <div style={{display:"flex",gap:6}}>
                    {Object.entries(CRITICALITY).map(([k,v])=>(
                      <button key={k} onClick={()=>setNewH(p=>({...p,criticality:k}))} style={{flex:1,padding:"10px 0",borderRadius:9,border:`1.5px solid ${newH.criticality===k?v.border:"#E8EAED"}`,background:newH.criticality===k?v.bg:"#F8F9FA",color:v.text,fontWeight:700,fontSize:13}}>{k}</button>
                    ))}
                  </div>
                </div>
              </div>
              <div><div style={lblSt}>Accent Color</div>
                <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
                  {PALETTE.map(c=><button key={c} onClick={()=>setNewH(p=>({...p,color:c}))} style={{width:28,height:28,borderRadius:"50%",background:c,border:newH.color===c?"3px solid #202124":"3px solid transparent",boxShadow:newH.color===c?`0 0 0 2px ${c}55`:"none"}}/>)}
                </div>
              </div>
              <button onClick={addHabit} style={{background:"#4285F4",border:"none",borderRadius:12,padding:"14px",color:"#fff",fontSize:15,fontWeight:700,marginTop:4,boxShadow:"0 2px 10px rgba(66,133,244,0.3)"}}>Create Habit</button>
            </div>
          </div>
        </div>
      )}

      {showAddC&&(
        <div className="modal-overlay" onClick={()=>setShowAddC(false)} style={{position:"fixed",inset:0,background:"rgba(32,33,36,0.48)",zIndex:100,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
          <div className="modal-box" onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:"22px 22px 0 0",padding:"22px 20px 32px",width:"100%",maxWidth:430,boxShadow:"0 -8px 40px rgba(0,0,0,0.14)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
              <span style={{fontSize:18,fontWeight:700,color:"#202124"}}>New Category</span>
              <button onClick={()=>setShowAddC(false)} style={{background:"#F1F3F4",border:"none",borderRadius:"50%",width:30,height:30,fontSize:16,color:"#5F6368",display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <div><div style={lblSt}>Name</div><input value={newC.name} onChange={e=>setNewC(p=>({...p,name:e.target.value}))} placeholder="e.g. Morning Routine" style={fldSt} autoFocus/></div>
              <div><div style={lblSt}>Icon</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                  {ICONS.map(ic=><button key={ic} onClick={()=>setNewC(p=>({...p,icon:ic}))} style={{background:newC.icon===ic?"#E8F0FE":"#F8F9FA",border:`1.5px solid ${newC.icon===ic?"#4285F4":"#E8EAED"}`,borderRadius:8,padding:"5px 7px",fontSize:18}}>{ic}</button>)}
                </div>
              </div>
              <div><div style={lblSt}>Category Color</div>
                <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
                  {PALETTE.map(c=><button key={c} onClick={()=>setNewC(p=>({...p,color:c}))} style={{width:28,height:28,borderRadius:"50%",background:c,border:newC.color===c?"3px solid #202124":"3px solid transparent",boxShadow:newC.color===c?`0 0 0 2px ${c}55`:"none"}}/>)}
                </div>
              </div>
              <button onClick={addCat} style={{background:"#4285F4",border:"none",borderRadius:12,padding:"14px",color:"#fff",fontSize:15,fontWeight:700,marginTop:4,boxShadow:"0 2px 10px rgba(66,133,244,0.3)"}}>Create Category</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
